import { useState, useCallback } from 'react'
import { getUsers } from '../services/userManagementApi'
import type { UserListItem, UserFilters } from '../types'
import { DEFAULT_FILTERS, SESSION_FILTER_KEY } from '../constants'

/**
 * Hook for managing the user management list — pagination, filters, sorting, session persistence.
 * Mirrors Angular UserManagementListComponent list state management.
 */
export function useUserManagementList() {
  const [users, setUsers] = useState<UserListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [sorting, setSorting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [currentFilters, setCurrentFilters] = useState<UserFilters | null>(null)

  // ─── Session persistence ──────────────────────────────────────────────────

  const loadFiltersFromSession = useCallback((): UserFilters => {
    try {
      const saved = sessionStorage.getItem(SESSION_FILTER_KEY)
      if (saved) return { ...DEFAULT_FILTERS, ...(JSON.parse(saved) as UserFilters) }
    } catch {
      // ignore
    }
    return { ...DEFAULT_FILTERS }
  }, [])

  const saveFiltersToSession = useCallback((filters: UserFilters) => {
    try {
      sessionStorage.setItem(SESSION_FILTER_KEY, JSON.stringify(filters))
    } catch {
      // ignore
    }
  }, [])

  const clearFiltersFromSession = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_FILTER_KEY)
    } catch {
      // ignore
    }
  }, [])

  const getEffectiveFilters = useCallback((): UserFilters => {
    return currentFilters ?? loadFiltersFromSession()
  }, [currentFilters, loadFiltersFromSession])

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async (filters: UserFilters, showLoading = true) => {
    if (showLoading) setLoading(true)
    setError(null)
    try {
      const response = await getUsers(filters)
      setUsers(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch users.'
      setError(msg)
      setUsers([])
    } finally {
      if (showLoading) setLoading(false)
      setSorting(false)
    }
  }, [])

  // ─── Actions ──────────────────────────────────────────────────────────────

  const applyFilters = useCallback(
    (newFilters: Partial<UserFilters>) => {
      const base = currentFilters ?? loadFiltersFromSession()
      const isOnlyPageChange = Object.keys(newFilters).length === 1 && 'page' in newFilters
      const pageValue =
        newFilters.page !== undefined ? newFilters.page : isOnlyPageChange ? base.page : 1
      const updated: UserFilters = { ...base, ...newFilters, page: pageValue }
      setCurrentFilters(updated)
      saveFiltersToSession(updated)
      void fetchUsers(updated)
    },
    [currentFilters, loadFiltersFromSession, saveFiltersToSession, fetchUsers]
  )

  const resetFilters = useCallback((overrides?: Partial<UserFilters>) => {
    clearFiltersFromSession()
    const defaults = overrides ? { ...DEFAULT_FILTERS, ...overrides } : { ...DEFAULT_FILTERS }
    setCurrentFilters(defaults)
    void fetchUsers(defaults)
  }, [clearFiltersFromSession, fetchUsers])

  const changePage = useCallback(
    (page: number) => applyFilters({ page }),
    [applyFilters]
  )

  const changePerPage = useCallback(
    (perPage: number) => applyFilters({ perPage, page: 1 }),
    [applyFilters]
  )

  const sort = useCallback(
    (orderBy: string) => {
      const base = currentFilters ?? loadFiltersFromSession()
      const newSortBy = base.sortBy === 'asc' ? 'desc' : 'asc'
      // Angular sort() -> triggerFilter() resets to page 1 before loading.
      const updated: UserFilters = { ...base, orderBy, sortBy: newSortBy, page: 1 }
      setCurrentFilters(updated)
      setSorting(true)
      saveFiltersToSession(updated)
      void fetchUsers(updated, true)
    },
    [currentFilters, loadFiltersFromSession, saveFiltersToSession, fetchUsers]
  )

  const refetch = useCallback(() => {
    void fetchUsers(getEffectiveFilters())
  }, [fetchUsers, getEffectiveFilters])

  /** Initialize on mount — called once by the page component. */
  const initialize = useCallback(
    (overrides?: Partial<UserFilters>) => {
      const base = loadFiltersFromSession()
      const filters = overrides ? { ...base, ...overrides } : base
      setCurrentFilters(filters)
      saveFiltersToSession(filters)
      void fetchUsers(filters)
    },
    [loadFiltersFromSession, saveFiltersToSession, fetchUsers]
  )

  return {
    users,
    loading,
    sorting,
    error,
    pagination,
    filters: getEffectiveFilters(),
    initialize,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  }
}
